"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NinErrorMessage = exports.NinErrorCode = void 0;
exports.validateNin = validateNin;
exports.validateNinWithSpaceStrict = validateNinWithSpaceStrict;
exports.validateNinStrict = validateNinStrict;
exports.isMale = isMale;
exports.isFemale = isFemale;
exports.isAgedBetween18And99 = isAgedBetween18And99;
exports.birthdayFrom = birthdayFrom;
exports.ninToYYYYMMDD = ninToYYYYMMDD;
exports.isNinAndDateOfBirthMatch = isNinAndDateOfBirthMatch;
const ValidationResult_1 = require("../ValidationResult");
const ninUtils_1 = require("../../utils/ninUtils");
const dateUtils_1 = require("../../utils/dateUtils");
const stringUtils_1 = require("../../utils/stringUtils");
var NinErrorCode;
(function (NinErrorCode) {
    NinErrorCode["NIN_MISSING"] = "NIN_MISSING";
    NinErrorCode["NIN_INVALID"] = "NIN_INVALID";
    NinErrorCode["NIN_INVALID_STRICT"] = "NIN_INVALID_STRICT";
})(NinErrorCode || (exports.NinErrorCode = NinErrorCode = {}));
var NinErrorMessage;
(function (NinErrorMessage) {
    NinErrorMessage["NIN_MISSING"] = "Du m\u00E5 fylle ut f\u00F8dselsnummer.";
    NinErrorMessage["NIN_INVALID"] = "F\u00F8dselsnummer m\u00E5 v\u00E6re 11 siffer.";
    NinErrorMessage["NIN_INVALID_STRICT"] = "F\u00F8dselsnummer m\u00E5 v\u00E6re 11 siffer og v\u00E6re p\u00E5 gyldig format.";
})(NinErrorMessage || (exports.NinErrorMessage = NinErrorMessage = {}));
function validateNin(nin) {
    if (!nin) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_MISSING, NinErrorMessage.NIN_MISSING);
    }
    else if (!ninUtils_1.ninFormatWithOptionalSpace.test(nin)) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_INVALID, NinErrorMessage.NIN_INVALID);
    }
    return (0, ValidationResult_1.success)();
}
function validateNinWithSpaceStrict(nin) {
    if (nin && ninUtils_1.ninFormatWithSpace.test(nin)) {
        const matches = nin.match(ninUtils_1.ninFormatWithSpace);
        if (matches && matches.length > 2) {
            return validateNinStrict(`${matches[1]}${matches[2]}`);
        }
    }
    return validateNinStrict(nin);
}
function validateNinStrict(nin) {
    const nonStrictValidation = validateNin(nin);
    if (!nonStrictValidation.valid || !nin) {
        return nonStrictValidation;
    }
    const day = (0, ninUtils_1.dayFrom)(nin);
    const month = (0, ninUtils_1.monthFrom)(nin);
    const year = (0, ninUtils_1.yearFrom)(nin);
    const individsifre = (0, ninUtils_1.individsifreFrom)(nin);
    const control1 = parseInt(nin.substring(9, 10), 10);
    const control2 = parseInt(nin.substring(10, 11), 10);
    const ninYear = (0, ninUtils_1.calculateNinYear)(year, parseInt(individsifre, 10));
    if (!ninYear) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_INVALID_STRICT, NinErrorMessage.NIN_INVALID_STRICT);
    }
    const monthNumber = Number(month);
    const jsDate = new Date(ninYear, monthNumber - 1, Number(day));
    if (!(0, dateUtils_1.isValidDate)(jsDate, monthNumber)) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_INVALID_STRICT, NinErrorMessage.NIN_INVALID_STRICT);
    }
    const calculatedControl1 = (0, ninUtils_1.calculateNinControl1)(day, month, year, individsifre);
    if (calculatedControl1 === undefined || calculatedControl1 !== control1) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_INVALID_STRICT, NinErrorMessage.NIN_INVALID_STRICT);
    }
    const calculatedControl2 = (0, ninUtils_1.calculateNinControl2)(day, month, year, individsifre, control1);
    if (calculatedControl2 === undefined || calculatedControl2 !== control2) {
        return (0, ValidationResult_1.error)(NinErrorCode.NIN_INVALID_STRICT, NinErrorMessage.NIN_INVALID_STRICT);
    }
    return (0, ValidationResult_1.success)();
}
function isMale(nin) {
    const nonStrictValidation = validateNin(nin);
    if (!nonStrictValidation.valid || !nin) {
        return undefined;
    }
    return (0, ninUtils_1.hasOddIndividsiffer3)((0, stringUtils_1.removeWhitespace)(nin));
}
function isFemale(nin) {
    const nonStrictValidation = validateNin(nin);
    if (!nonStrictValidation.valid || !nin) {
        return undefined;
    }
    return !(0, ninUtils_1.hasOddIndividsiffer3)((0, stringUtils_1.removeWhitespace)(nin));
}
function isAgedBetween18And99(nin) {
    var _a;
    const strictValidation = validateNinWithSpaceStrict(nin);
    if (!strictValidation.valid || !nin) {
        return false;
    }
    const trimmedNin = (0, stringUtils_1.removeWhitespace)(nin);
    const currentYear = new Date().getFullYear();
    const birthYear = (_a = birthdayFrom(trimmedNin)) === null || _a === void 0 ? void 0 : _a.getFullYear();
    if (!birthYear) {
        return false;
    }
    const diff = currentYear - birthYear;
    if (diff >= 18 && diff <= 99) {
        return true;
    }
    return false;
}
function birthdayFrom(nin) {
    const strictValidation = validateNinWithSpaceStrict(nin);
    if (!strictValidation.valid || !nin) {
        return undefined;
    }
    const trimmedNin = (0, stringUtils_1.removeWhitespace)(nin);
    const year = (0, ninUtils_1.calculateNinYear)((0, ninUtils_1.yearFrom)(trimmedNin), parseInt((0, ninUtils_1.individsifreFrom)(trimmedNin), 10));
    if (!year) {
        return undefined;
    }
    return new Date(year, parseInt((0, ninUtils_1.monthFrom)(trimmedNin), 10) - 1, parseInt((0, ninUtils_1.dayFrom)(trimmedNin), 10));
}
function ninToYYYYMMDD(nin) {
    const birthdayAsDate = birthdayFrom(nin);
    if (!birthdayAsDate) {
        return undefined;
    }
    return (0, dateUtils_1.dateToYYYYMMDD)(birthdayAsDate);
}
function isNinAndDateOfBirthMatch(nin, dateOfBirthYYYYMMDD) {
    const birthdayAsDate = birthdayFrom(nin);
    if (!birthdayAsDate || !dateOfBirthYYYYMMDD) {
        return false;
    }
    if (birthdayAsDate && dateOfBirthYYYYMMDD) {
        if (!dateOfBirthYYYYMMDD.match(dateUtils_1.regexYYYYMMDD)) {
            return true;
        }
        return ninToYYYYMMDD(nin) === dateOfBirthYYYYMMDD;
    }
    return false;
}
//# sourceMappingURL=ninValidator.js.map